import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { FaShoppingCart, FaStar, FaSearch, FaFilter, FaArrowLeft, FaHeart, FaFire, FaTruck, FaShieldAlt, FaPercent, FaBox, FaTools, FaHardHat } from 'react-icons/fa';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Placeholder images by category
const categoryPlaceholders = {
  suplementos: 'https://images.unsplash.com/photo-1593095948071-474c5cc2989d?w=400&h=400&fit=crop',
  ropa: 'https://images.unsplash.com/photo-1506629082955-511b1aa562c8?w=400&h=400&fit=crop',
  accesorios: 'https://images.unsplash.com/photo-1598289431512-b97b0917affc?w=400&h=400&fit=crop',
  equipos: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=400&h=400&fit=crop'
};

// Componente de página en construcción
const UnderConstructionPage = ({ onBack }) => (
  <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0a0a0a' }}>
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-center p-8 max-w-lg"
    >
      <div className="w-32 h-32 mx-auto mb-6 rounded-full flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg, #FF9500, #FF6B00)' }}>
        <FaHardHat className="text-5xl text-white" />
      </div>
      <h1 className="text-4xl font-bold text-white mb-4">
        🚧 En Construcción 🚧
      </h1>
      <p className="text-xl text-gray-300 mb-6">
        Estamos trabajando para traerte la mejor experiencia de compra.
        ¡Pronto estará disponible!
      </p>
      <div className="flex items-center justify-center gap-4 mb-8">
        <FaTools className="text-2xl text-orange-400 animate-bounce" />
        <span className="text-orange-400 font-semibold">Mejorando para ti</span>
        <FaTools className="text-2xl text-orange-400 animate-bounce" style={{ animationDelay: '0.2s' }} />
      </div>
      <Button
        onClick={onBack}
        className="px-8 py-3 text-white font-semibold"
        style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }}
      >
        <FaArrowLeft className="mr-2" />
        Volver al Inicio
      </Button>
    </motion.div>
  </div>
);

const Tienda = () => {
  const navigate = useNavigate();
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userPlan, setUserPlan] = useState('none');
  const [selectedCategory, setSelectedCategory] = useState('todos');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('featured');
  const [cart, setCart] = useState([]);
  const [hoveredProduct, setHoveredProduct] = useState(null);
  const [isUnderConstruction, setIsUnderConstruction] = useState(false);

  const categories = [
    { id: 'todos', name: 'Todos', icon: <FaFire />, color: 'from-pink-500 to-rose-500' },
    { id: 'suplementos', name: 'Suplementos', icon: '💪', color: 'from-blue-500 to-cyan-500' },
    { id: 'ropa', name: 'Ropa', icon: '👕', color: 'from-purple-500 to-pink-500' },
    { id: 'accesorios', name: 'Accesorios', icon: '🎯', color: 'from-amber-500 to-orange-500' },
    { id: 'equipos', name: 'Equipos', icon: '🏋️', color: 'from-green-500 to-emerald-500' }
  ];

  useEffect(() => {
    checkUnderConstruction();
    fetchProductos();
    fetchUserInfo();
  }, []);

  const checkUnderConstruction = async () => {
    try {
      const response = await axios.get(`${API}/public/sections-status`);
      if (response.data.tienda === true) {
        setIsUnderConstruction(true);
      }
    } catch (error) {
      console.error('Error checking construction status:', error);
    }
  };

  const fetchUserInfo = async () => {
    try {
      const response = await axios.get(`${API}/user/profile`, { withCredentials: false });
      setUserPlan(response.data.subscription_plan || 'none');
    } catch (error) {
      console.error('Error fetching user info:', error);
      setUserPlan('none');
    }
  };

  const fetchProductos = async () => {
    try {
      const response = await axios.get(`${API}/productos`);
      if (response.data.productos && response.data.productos.length > 0) {
        setProductos(response.data.productos);
      } else {
        // Fallback sample products
        setProductos([
          {
            id: '1',
            name: 'Proteína Whey Premium',
            description: 'Proteína de suero de alta calidad, 25g de proteína por porción. Ideal para recuperación muscular.',
            price: 159000,
            original_price: 199000,
            category: 'suplementos',
            thumbnail_url: null,
            stock: 15,
            rating: 4.8,
            reviews: 234,
            tags: ['bestseller', 'nuevo']
          },
          {
            id: '2',
            name: 'Leggings de Compresión Pro',
            description: 'Leggings de alta compresión para máximo rendimiento y comodidad.',
            price: 129000,
            original_price: 179000,
            category: 'ropa',
            thumbnail_url: null,
            stock: 25,
            rating: 4.9,
            reviews: 189,
            tags: ['bestseller']
          }
        ]);
      }
    } catch (error) {
      console.error('Error fetching productos:', error);
      toast.error('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item => 
          item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
    toast.success(`${product.name} agregado al carrito`);
  };

  const getDiscount = (original, current) => {
    if (!original || original <= current) return 0;
    return Math.round(((original - current) / original) * 100);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(price);
  };

  const getProductImage = (product) => {
    if (product.thumbnail_url) return product.thumbnail_url;
    return categoryPlaceholders[product.category] || categoryPlaceholders.suplementos;
  };

  const filteredProducts = productos
    .filter(p => selectedCategory === 'todos' || p.category === selectedCategory)
    .filter(p => !searchQuery || p.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === 'price-low') return a.price - b.price;
      if (sortBy === 'price-high') return b.price - a.price;
      if (sortBy === 'rating') return (b.rating || 0) - (a.rating || 0);
      return 0;
    });

  // Si está en construcción, mostrar página de construcción
  if (isUnderConstruction) {
    return <UnderConstructionPage onBack={() => navigate('/dashboard')} />;
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="tienda-page">
      {/* Hero Section - Con fotos de físicos */}
      <div className="relative overflow-hidden min-h-[500px]">
        {/* Grid de Fotos de Fondo */}
        <div className="absolute inset-0 grid grid-cols-2 md:grid-cols-4">
          {[
            'https://customer-assets.emergentagent.com/job_fitfolio-app/artifacts/1a80whuy_11.png',
            'https://customer-assets.emergentagent.com/job_fitfolio-app/artifacts/f2rs90dv_8.png',
            'https://customer-assets.emergentagent.com/job_fitfolio-app/artifacts/m2qqkp2r_9.png',
            'https://customer-assets.emergentagent.com/job_fitfolio-app/artifacts/r2dt5e5l_10.png'
          ].map((url, idx) => (
            <div key={idx} className="relative overflow-hidden">
              <img 
                src={url} 
                alt={`Físico ${idx + 1}`}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
        </div>
        
        {/* Overlay oscuro para legibilidad */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.7), rgba(10,10,10,0.9))' }} />
        
        <div className="relative max-w-7xl mx-auto px-4 py-12 md:py-20">
          {/* Navigation */}
          <div className="flex items-center justify-between mb-8">
            <Button 
              onClick={() => navigate('/dashboard')} 
              className="text-white hover:opacity-80 backdrop-blur-sm rounded-full px-5 py-2"
              style={{ 
                background: 'transparent',
                border: '2px solid var(--brand-primary)',
                color: 'var(--brand-primary)'
              }}
              data-testid="back-button"
            >
              <FaArrowLeft className="mr-2" /> Volver
            </Button>
            
            <Button 
              className="text-white relative font-semibold rounded-full px-6"
              style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }}
              onClick={() => toast.info(`Carrito: ${cart.length} productos`)}
              data-testid="cart-button"
            >
              <FaShoppingCart className="mr-2" />
              Carrito
              {cart.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-black text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                  {cart.reduce((sum, item) => sum + item.quantity, 0)}
                </span>
              )}
            </Button>
          </div>
          
          {/* Hero Content */}
          <div className="text-center space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span 
                className="inline-block px-4 py-1 backdrop-blur-sm rounded-full text-sm font-medium mb-4 text-white"
                style={{ background: 'rgba(var(--brand-primary-glow), 0.3)' }}
              >
                🔥 Envío Gratis en compras +$200.000
              </span>
            </motion.div>
            
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-5xl md:text-7xl font-black tracking-tight uppercase text-white drop-shadow-2xl"
              style={{ fontFamily: 'Poppins, sans-serif', letterSpacing: '0.03em' }}
            >
              TIENDA{' '}
              <span 
                className="bg-clip-text text-transparent"
                style={{ backgroundImage: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
              >
                DEISYCFIT
              </span>
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl md:text-2xl text-white/90 max-w-2xl mx-auto"
            >
              Productos premium para potenciar tu transformación fitness
            </motion.p>
            
            {/* Search Bar */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="max-w-xl mx-auto relative mt-8"
            >
              <FaSearch className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-500 text-lg" />
              <Input
                placeholder="¿Qué estás buscando?"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-14 py-7 text-lg bg-white/10 backdrop-blur-xl text-white placeholder:text-gray-400 rounded-full border border-white/20 focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary)]/30"
                data-testid="search-input"
              />
            </motion.div>
          </div>
        </div>
      </div>

      {/* Benefits Bar */}
      <div className="bg-zinc-900 border-y border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 py-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: <FaTruck />, text: 'Envío Gratis +$200.000' },
              { icon: <FaShieldAlt />, text: 'Garantía de Calidad' },
              { icon: <FaPercent />, text: 'Descuentos VIP' },
              { icon: <FaStar />, text: 'Productos Premium' }
            ].map((benefit, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * idx }}
                className="flex items-center justify-center gap-3 text-sm"
              >
                <span className="text-pink-500 text-lg">{benefit.icon}</span>
                <span className="text-gray-300">{benefit.text}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Category Filters - Pills Style */}
        <div className="mb-10">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((cat, idx) => (
              <motion.button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="px-6 py-3 rounded-full font-medium transition-all duration-300 flex items-center gap-2 text-white"
                style={selectedCategory === cat.id 
                  ? { background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }
                  : { background: 'rgba(39, 39, 42, 0.8)' }
                }
                data-testid={`category-${cat.id}`}
              >
                <span className="text-lg">{typeof cat.icon === 'string' ? cat.icon : cat.icon}</span>
                <span>{cat.name}</span>
              </motion.button>
            ))}
          </div>
        </div>

        {/* Sort & Count */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-8">
          <p className="text-gray-400 text-lg">
            <span className="text-white font-bold">{filteredProducts.length}</span> productos encontrados
          </p>
          <div className="flex items-center gap-3">
            <FaFilter className="text-gray-400" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-zinc-800 text-white px-5 py-3 rounded-xl border border-zinc-700 focus:ring-2 focus:ring-[var(--brand-primary)]"
              data-testid="sort-select"
            >
              <option value="featured">Destacados</option>
              <option value="price-low">Precio: Menor a Mayor</option>
              <option value="price-high">Precio: Mayor a Menor</option>
              <option value="rating">Mejor Valorados</option>
            </select>
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-[var(--brand-primary)] border-t-transparent mb-4" />
            <p className="text-gray-400">Cargando productos...</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-20">
            <FaBox className="text-6xl text-gray-600 mx-auto mb-4" />
            <p className="text-2xl text-gray-400 mb-2">No se encontraron productos</p>
            <p className="text-gray-500 mb-6">Intenta con otra búsqueda o categoría</p>
            <Button 
              onClick={() => { setSelectedCategory('todos'); setSearchQuery(''); }}
              className="text-white rounded-full font-semibold"
              style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }}
            >
              Ver todos los productos
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            <AnimatePresence>
              {filteredProducts.map((product, index) => {
                const discount = getDiscount(product.original_price, product.price);
                const isHovered = hoveredProduct === product.id;
                
                return (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ delay: index * 0.05 }}
                    onMouseEnter={() => setHoveredProduct(product.id)}
                    onMouseLeave={() => setHoveredProduct(null)}
                    onClick={() => navigate(`/tienda/producto/${product.id}`)}
                    className="group cursor-pointer"
                    data-testid={`product-card-${product.id}`}
                  >
                    <Card className="bg-zinc-900 border-zinc-800 overflow-hidden hover:border-pink-500/50 transition-all duration-500 h-full hover:shadow-xl hover:shadow-pink-500/10">
                      {/* Image Container */}
                      <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-zinc-800 to-zinc-900">
                        <img
                          src={getProductImage(product)}
                          alt={product.name}
                          className={`w-full h-full object-cover transition-all duration-700 ${isHovered ? 'scale-110' : 'scale-100'}`}
                          onError={(e) => {
                            e.target.src = categoryPlaceholders[product.category] || categoryPlaceholders.suplementos;
                          }}
                        />
                        
                        {/* Gradient Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                        
                        {/* Badges */}
                        <div className="absolute top-3 left-3 flex flex-col gap-2">
                          {discount > 0 && (
                            <Badge 
                              className="text-white px-3 py-1.5 font-bold text-sm shadow-lg"
                              style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }}
                            >
                              -{discount}%
                            </Badge>
                          )}
                          {product.tags?.includes('bestseller') && (
                            <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-3 py-1.5 font-bold text-sm shadow-lg">
                              <FaFire className="mr-1 inline" /> TOP
                            </Badge>
                          )}
                          {product.tags?.includes('nuevo') && (
                            <Badge className="bg-emerald-500 text-white px-3 py-1.5 font-bold text-sm shadow-lg">
                              NUEVO
                            </Badge>
                          )}
                        </div>
                        
                        {/* Wishlist Button */}
                        <button 
                          className="absolute top-3 right-3 w-10 h-10 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all"
                          style={{ ':hover': { background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' } }}
                          data-testid={`wishlist-${product.id}`}
                        >
                          <FaHeart />
                        </button>
                        
                        {/* Quick Add - Slides up on hover */}
                        <div className={`absolute bottom-0 left-0 right-0 p-4 transition-all duration-300 ${isHovered ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`}>
                          <Button 
                            onClick={() => addToCart(product)}
                            className="w-full text-white font-bold py-3 shadow-xl rounded-full"
                            style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }}
                            data-testid={`add-to-cart-${product.id}`}
                          >
                            <FaShoppingCart className="mr-2" /> Agregar al Carrito
                          </Button>
                        </div>
                      </div>
                      
                      {/* Content */}
                      <CardContent className="p-5">
                        {/* Category */}
                        <p className="text-pink-500 text-xs font-bold uppercase tracking-wider mb-2">
                          {categories.find(c => c.id === product.category)?.name || product.category}
                        </p>
                        
                        {/* Name */}
                        <h3 className="text-white font-bold text-lg mb-2 line-clamp-2 group-hover:text-pink-400 transition-colors min-h-[3.5rem]">
                          {product.name}
                        </h3>
                        
                        {/* Description */}
                        <p className="text-gray-400 text-sm mb-4 line-clamp-2 min-h-[2.5rem]">
                          {product.description}
                        </p>
                        
                        {/* Rating */}
                        {product.rating && (
                          <div className="flex items-center gap-2 mb-4">
                            <div className="flex items-center">
                              {[...Array(5)].map((_, i) => (
                                <FaStar 
                                  key={i} 
                                  className={`text-sm ${i < Math.floor(product.rating) ? 'text-amber-400' : 'text-zinc-700'}`} 
                                />
                              ))}
                            </div>
                            <span className="text-gray-400 text-sm">
                              {product.rating} ({product.reviews || 0})
                            </span>
                          </div>
                        )}
                        
                        {/* Price */}
                        <div className="flex items-end gap-3">
                          <span className="text-2xl font-black text-white">
                            {formatPrice(product.price)}
                          </span>
                          {product.original_price > product.price && (
                            <span className="text-gray-500 line-through text-sm mb-1">
                              {formatPrice(product.original_price)}
                            </span>
                          )}
                        </div>
                        
                        {/* Stock Warning */}
                        {product.stock !== undefined && product.stock < 10 && product.stock > 0 && (
                          <p className="text-amber-500 text-xs mt-3 font-medium">
                            ⚡ ¡Solo quedan {product.stock} unidades!
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Newsletter Section */}
      <div className="py-16 mt-12" style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-black mb-4 text-white uppercase" style={{ fontFamily: 'Poppins, sans-serif' }}>
              ÚNETE A LA COMUNIDAD
            </h2>
            <p className="text-white/80 mb-8 text-lg">
              Recibe ofertas exclusivas, nuevos productos y tips de entrenamiento
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <Input
                placeholder="Tu email"
                className="bg-white/10 text-white placeholder:text-white/50 rounded-full border border-white/30 px-6 py-6 flex-1"
              />
              <Button 
                className="text-white hover:opacity-90 rounded-full px-8 py-6 font-bold"
                style={{ background: 'rgba(0, 0, 0, 0.8)' }}
              >
                Suscribirse
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Tienda;
